import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { useGestorPerformance } from '@/hooks/useGestorPerformance';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import PerformanceSummaryCards from '@/components/dashboard/gestor/PerformanceSummaryCards';
import HubEfficiencyChart from '@/components/dashboard/gestor/HubEfficiencyChart';
import DelayedInspectionsChart from '@/components/dashboard/gestor/DelayedInspectionsChart';
import GovernanceChart from '@/components/dashboard/gestor/GovernanceChart';
import TeamPerformanceChart from '@/components/dashboard/gestor/TeamPerformanceChart';
import CapacityUtilizationChart from '@/components/dashboard/gestor/CapacityUtilizationChart';
import HubRankingTable from '@/components/dashboard/gestor/HubRankingTable';

const GestorPerformancePage: React.FC = () => {
  const { loading, metrics, refetch } = useGestorPerformance();

  if (loading) {
    return (
      <MainLayout title="Desempenho de Gestores" subtitle="Métricas e indicadores de performance">
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => (
              <Card key={i}><CardContent className="p-4"><Skeleton className="h-16" /></CardContent></Card>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}><CardContent className="p-4"><Skeleton className="h-[250px]" /></CardContent></Card>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!metrics) {
    return (
      <MainLayout title="Desempenho de Gestores" subtitle="Métricas e indicadores de performance">
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          Nenhum dado disponível
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout 
      title="Desempenho de Gestores" 
      subtitle="Métricas e indicadores de performance"
    >
      <div className="flex justify-end mb-4">
        <Button variant="outline" size="sm" onClick={refetch} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* KPIs Resumo */}
      <PerformanceSummaryCards totais={metrics.totais} />

      {/* Gráficos - Eficiência Operacional */}
      <h3 className="text-sm font-semibold text-foreground mt-6 mb-3 uppercase tracking-wide">
        Eficiência Operacional
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HubEfficiencyChart data={metrics.hubsPerformance} />
        <DelayedInspectionsChart data={metrics.inspecoesAtrasadas} />
      </div>

      {/* Gráficos - Recursos e Capacidade */}
      <h3 className="text-sm font-semibold text-foreground mt-6 mb-3 uppercase tracking-wide">
        Recursos e Capacidade
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CapacityUtilizationChart data={metrics.hubsPerformance} />
        <TeamPerformanceChart data={metrics.profissionaisPerformance} />
      </div>

      {/* Governança e Ranking */}
      <h3 className="text-sm font-semibold text-foreground mt-6 mb-3 uppercase tracking-wide">
        Governança e Ranking
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GovernanceChart data={metrics.relatoriosGovernanca} />
        <HubRankingTable data={metrics.hubsPerformance} />
      </div>

      {/* Recomendações */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold mb-2">Métricas Prioritárias para Acompanhamento</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Taxa de Conclusão</strong> - Principal indicador de eficiência operacional</li>
            <li>• <strong>Inspeções Atrasadas</strong> - Impacto direto na qualidade do serviço</li>
            <li>• <strong>Relatórios Pendentes</strong> - Governança e conformidade</li>
            <li>• <strong>Utilização de Capacidade</strong> - Otimização de recursos</li>
          </ul>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default GestorPerformancePage;
